import React, { useEffect, useState } from "react";
import { FaKey } from "react-icons/fa";
import { apihubGet } from "../../api/apihub";
import { useProjectPhone } from "../../hooks/useProjectPhone";



const ApiKeyGenerate = () => {
  const { projectId } = useProjectPhone();
  const [apiKey, setApiKey] = useState("");
  const [loading, setLoading] = useState(true);
  const [regenerating, setRegenerating] = useState(false);
  const [showDocs, setShowDocs] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!projectId) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const res = await apihubGet(
          `/getInteractedCustomerDetail/getuserapikey/${projectId}`,
        );
        if (!cancelled) setApiKey(res?.APIKey || res?.apiKey || "");
      } catch {
        if (!cancelled) setApiKey("");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [projectId]);

  const copyKey = () => {
    if (!apiKey) return;
    navigator.clipboard.writeText(apiKey).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  const regenerate = async () => {
    if (!projectId) return;
    setRegenerating(true);
    try {
      const res = await apihubGet(
        `/getInteractedCustomerDetail/regenerateapikey/${projectId}`,
      );
      setApiKey(res?.APIKey || res?.apiKey || "");
    } catch {
      alert(
        "Could not regenerate API key. Sign in to apihub with a valid token.",
      );
    } finally {
      setRegenerating(false);
    }
  };

  return (
    <div className="biz-panel-page biz-apikey-page">
      <div className="biz-panel-header">
        <h1>
          <FaKey />
          API Key
        </h1>
      </div>

      <div className="biz-apikey-card">
        <h2>API Key</h2>
        {loading ? (
          <p className="biz-apikey-loading">Loading…</p>
        ) : (
          <p className="biz-apikey-value">
            {apiKey || "No API Key available — log in to apihub to load your key."}
          </p>
        )}
        <div className="biz-apikey-actions">
          <button
            type="button"
            className="biz-btn-blue"
            onClick={copyKey}
            disabled={!apiKey}
          >
            {copied ? "Copied!" : "Copy to Clipboard"}
          </button>
          <button
            type="button"
            className="biz-btn-green"
            onClick={regenerate}
            disabled={regenerating}
          >
            {regenerating ? "Regenerating…" : "Regenerate Key"}
          </button>
        </div>
        <button
          type="button"
          className="biz-btn-dark"
          onClick={() => setShowDocs((v) => !v)}
        >
          {showDocs ? "Hide API Docs" : "Read API Docs"}
        </button>

        {showDocs && (
          <div className="biz-apikey-docs">
            <h3>Get Templates</h3>
            <p>
              Returns approved WhatsApp templates for the project tied to your
              API key. Send the key in the <code>apiKey</code> header. No login
              token is required.
            </p>
            <pre className="biz-apikey-curl">{`POST /getInteractedCustomerDetail/getTemplates
apiKey: YOUR_API_KEY
Content-Type: application/json`}</pre>
            <table className="biz-table biz-apikey-fields">
              <thead>
                <tr>
                  <th>Header</th>
                  <th>Type</th>
                  <th>Description</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>apiKey</td>
                  <td>string</td>
                  <td>Required. API key generated from this page.</td>
                </tr>
              </tbody>
            </table>

            <h3>Send Template</h3>
            <p>
              Send an approved WhatsApp template. Only non-empty{" "}
              <code>input_var_1</code>, <code>input_var_2</code>, and{" "}
              <code>input_var_3</code> values are passed to Graph as body
              variables. Each attempt is stored in <code>wa_api_data</code>.
            </p>
            <pre className="biz-apikey-curl">{`POST /getInteractedCustomerDetail/sendTemplate
apiKey: YOUR_API_KEY
Content-Type: application/json

{
  "id": "TEMPLATE_ID",
  "name": "welcome",
  "to": "919876543210",
  "input_var_1": "Rahul",
  "input_var_2": "Gneiss",
  "input_var_3": null
}`}</pre>
            <table className="biz-table biz-apikey-fields">
              <thead>
                <tr>
                  <th>Field</th>
                  <th>Type</th>
                  <th>Description</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>apiKey (header)</td>
                  <td>string</td>
                  <td>Required. Send in the apiKey header.</td>
                </tr>
                <tr>
                  <td>id</td>
                  <td>Number</td>
                  <td>Template id from getTemplates.</td>
                </tr>
                <tr>
                  <td>name</td>
                  <td>string</td>
                  <td>Approved template name.</td>
                </tr>
                <tr>
                  <td>data</td>
                  <td>string</td>
                  <td>Required. Recipient WhatsApp number (saved in wa_api_data.wa_number).</td>
                </tr>
                <tr>
                  <td>input_var_1</td>
                  <td>string</td>
                  <td>Optional. Template {"{{1}}"}.</td>
                </tr>
                <tr>
                  <td>input_var_2</td>
                  <td>string</td>
                  <td>Optional. Template {"{{2}}"}.</td>
                </tr>
                <tr>
                  <td>input_var_3</td>
                  <td>string</td>
                  <td>Optional. Template {"{{3}}"}.</td>
                </tr>
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default ApiKeyGenerate;
